import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { AccountDocument } from '../types/account';
import apiService from '../services/api';
import { format } from 'date-fns';

const NAVY = '#0d1f3c';

const CATEGORY_LABELS: Record<string, string> = {
  order_confirmation: 'Order Confirmation',
  contract: 'Contract',
  invoice: 'Invoice',
  id_document: 'ID Document',
  other: 'Other',
};

const MANUAL_CATEGORIES = [
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'id_document', label: 'ID Document' },
  { value: 'other', label: 'Other' },
];

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  accountId: string;
}

export default function AccountDocumentsTab({ accountId }: Props) {
  const [docs, setDocs] = useState<AccountDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfModal, setPdfModal] = useState<{ label: string; src: string } | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<AccountDocument | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<AccountDocument | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('other');
  const [editDescription, setEditDescription] = useState('');

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      setDocs(await apiService.listAccountDocuments(accountId));
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { void loadDocs(); }, [loadDocs]);

  const openUpload = () => {
    setUploadFile(null);
    setUploadName('');
    setUploadCategory('other');
    setUploadDescription('');
    setError('');
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) { setError('File and name are required.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiService.uploadAccountDocument(accountId, uploadFile, uploadName.trim(), uploadCategory, uploadDescription.trim());
      setUploadOpen(false);
      void loadDocs();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (doc: AccountDocument) => {
    setEditDoc(doc);
    setEditName(doc.name);
    setEditCategory(doc.category);
    setEditDescription(doc.description || '');
    setError('');
  };

  const handleEdit = async () => {
    if (!editDoc || !editName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await apiService.updateAccountDocument(accountId, editDoc.id, {
        name: editName.trim(),
        category: editCategory,
        description: editDescription.trim(),
      });
      setEditDoc(null);
      void loadDocs();
    } catch {
      setError('Update failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setSaving(true);
    try {
      await apiService.deleteAccountDocument(accountId, deleteDoc.id);
      setDeleteDoc(null);
      void loadDocs();
    } catch {
      setError('Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (doc: AccountDocument) => {
    const url = apiService.accountDocumentFileUrl(accountId, doc.id);
    const isPdf = (doc.content_type || '').includes('pdf');
    if (isPdf) {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blobUrl = await apiService.fetchDocumentBlobUrl(url);
      blobUrlRef.current = blobUrl;
      setPdfModal({ label: doc.name, src: blobUrl });
    } else {
      window.open(url, '_blank');
    }
  };

  const systemDocs = docs.filter((d) => d.document_source === 'system');
  const manualDocs = docs.filter((d) => d.document_source === 'manual');

  return (
    <Box>
      {/* System Documents */}
      {systemDocs.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
            System Documents
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {systemDocs.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>
                      <Chip label={CATEGORY_LABELS[doc.category] || doc.category} size="small" />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {formatBytes(doc.file_size_bytes)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {doc.created_at ? format(new Date(doc.created_at), 'dd MMM yyyy') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => openFile(doc)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Manual Documents */}
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700 }}>
            Manual Documents
          </Typography>
          <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={openUpload}>
            Upload Document
          </Button>
        </Box>

        {manualDocs.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No documents uploaded yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Uploaded</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {manualDocs.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>
                      <Chip label={CATEGORY_LABELS[doc.category] || doc.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {doc.description || '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {formatBytes(doc.file_size_bytes)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {doc.created_at ? format(new Date(doc.created_at), 'dd MMM yyyy') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => openFile(doc)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(doc)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteDoc(doc)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* PDF Viewer Modal */}
      <Dialog open={!!pdfModal} onClose={() => { setPdfModal(null); if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; } }} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={700} color={NAVY}>{pdfModal?.label}</Typography>
          <IconButton onClick={() => setPdfModal(null)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {pdfModal && (
            <Box component="iframe" src={pdfModal.src} sx={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }} title={pdfModal.label} />
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Button variant="outlined" component="label">
              {uploadFile ? uploadFile.name : 'Choose file'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setUploadFile(f);
                  if (f && !uploadName) setUploadName(f.name.replace(/\.[^.]+$/, ''));
                }}
              />
            </Button>
            <TextField
              label="Document name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <TextField
              select
              label="Category"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              size="small"
              fullWidth
            >
              {MANUAL_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description (optional)"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={saving || !uploadFile || !uploadName.trim()}>
            {saving ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDoc} onClose={() => setEditDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Document name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <TextField
              select
              label="Category"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              size="small"
              fullWidth
            >
              {MANUAL_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description (optional)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoc(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving || !editName.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteDoc} onClose={() => setDeleteDoc(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDoc?.name}</strong>? This cannot be undone.
          </Typography>
          {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDoc(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
